/*
 *  -----------------------------------------------------------------------
 *    <copyright file="data-set-expandable-grid.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { orderBy } from '@progress/kendo-data-query';

import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import { useGridCells, useTranslation } from 'hooks';
import { DataSetFields } from 'queries/generated/fragments.generated';
import ExpandableGrid from 'views/components/core/expandable/grid';
import { GridColumnMenuActiveFilter } from 'views/components/core/grid-filters';
import './admin-study-data-sets.scss';

interface DatasetExpandableGridTypes {
    data: {
        dataSets: DataSetFields[];
    };
    actions: (dataSet: DataSetFields) => {
        label: string;
        icon: string[];
        onClick: () => void;
        enabled: boolean;
    }[];
}

export const DatasetExpandableGrid: React.FC<DatasetExpandableGridTypes> = ({ data, actions }) => {
    const { YesNoCell, DateCell, MaxLengthCell } = useGridCells();
    const { t } = useTranslation();
    const [datasets, setDatasets] = useState([]);

    useEffect(() => {
        if (data && data.dataSets) {
            const newData = restructuredUpdatedCreatedBy(data.dataSets);
            setDatasets(orderBy(newData, [{ field: 'dataCutDate', dir: 'desc' }]));
        }
    }, [data]);

    return (
        <div className="expandable-grid-wrapper">
            <ExpandableGrid
                customKey="datasets-list-grid"
                id="datasets-list-grid"
                key="datasets-list"
                items={datasets}
                rowActions={actions}
                groupable={false}
                showPager
                scrollable="scrollable"
                reorderable
                pageable={false}
                addScroll
                resizable
                options={{
                    allowExcelExport: false,
                    allowExpandCollapseAll: false,
                    useFilterChips: false,
                }}
                columns={[
                    {
                        label: t('uam:dataCutDate'),
                        field: 'dataCutDate',
                        id: 'dataset-list-date',
                        minWidth: 170,
                        cells: { data: DateCell },
                        lockedByDefault: true,
                        filter: 'date',
                    },
                    {
                        label: t('uam:visible'),
                        field: 'active',
                        id: 'dataset-list-active',
                        minWidth: 130,
                        cells: { data: YesNoCell },
                        filterUi: GridColumnMenuActiveFilter,
                    },
                    {
                        label: t('uam:notes'),
                        field: 'notes',
                        id: 'dataset-list-notes',
                        minWidth: 200,
                        cells: { data: MaxLengthCell(150) },
                    },
                    {
                        label: t('ra:createdBy'),
                        field: '_createdByString',
                        id: 'dataset-list-createdBy',
                        minWidth: 200,
                    },
                    {
                        label: t('ra:createdDate'),
                        field: 'created',
                        id: 'dataset-list-created',
                        minWidth: 175,
                        cells: { data: DateCell },
                        filter: 'date',
                    },
                    {
                        label: t('ra:updatedBy'),
                        field: '_updatedByString',
                        id: 'dataset-list-updatedby',
                        minWidth: 200,
                    },
                    {
                        label: t('ra:updatedDate'),
                        field: 'updated',
                        id: 'dataset-list-updated',
                        minWidth: 175,
                        cells: { data: DateCell },
                        filter: 'date',
                    },
                    {
                        label: t('uam:recordId'),
                        field: 'recordId',
                        id: 'dataset-list-id',
                        minWidth: 100,
                        filter: 'numeric',
                        showByDefault: false,
                    },
                ]}
            />
        </div>
    );
};
